import { Link } from 'react-router-dom';
import { Target, Eye, Users, Award, Heart, ArrowRight } from 'lucide-react';
import { useSeo } from '../lib/seo';
import { useSiteSettings } from '../lib/site-settings';
import { getAboutPageContent } from '../lib/about-page-content';

export default function About() {
  const { settings, loading } = useSiteSettings();
  const content = getAboutPageContent(settings);

  useSeo({
    title: 'About Kisapuk Community Initiative',
    description: 'Learn about Kisapuk Community Initiative’s mission, values, milestones, and how we empower communities in Kenya.',
    pathname: '/about',
    image: 'https://your-domain.com/about-social.png',
  });
  const valueIcons = [Heart, Target, Users, Award];

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-brand-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={content.heroImageUrl} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="relative container-prose text-center">
          <span className="badge-gold mb-4">About Us</span>
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
            {content.heroTitle}
          </h1>
          <p className="mt-6 text-lg text-brand-100 max-w-2xl mx-auto">
            {content.heroSubtitle}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 lg:py-28">
        <div className="container-prose">
          <div className="grid gap-8 md:grid-cols-2">
            <div className="card p-8 lg:p-10">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-600 mb-6">
                <Target className="h-7 w-7" />
              </div>
              <h2 className="font-display text-2xl font-bold text-stone-900">{content.missionTitle}</h2>
              <p className="mt-4 text-stone-600 leading-relaxed">
                {content.missionText}
              </p>
            </div>
            <div className="card p-8 lg:p-10">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gold-100 text-gold-600 mb-6">
                <Eye className="h-7 w-7" />
              </div>
              <h2 className="font-display text-2xl font-bold text-stone-900">{content.visionTitle}</h2>
              <p className="mt-4 text-stone-600 leading-relaxed">
                {content.visionText}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-stone-100">
        <div className="container-prose">
          <div className="mx-auto max-w-2xl text-center mb-14">
            <span className="badge-brand mb-4">Our Values</span>
            <h2 className="font-display text-3xl font-bold text-stone-900 sm:text-4xl">
              {content.valuesHeading}
            </h2>
            {content.valuesIntro ? <p className="mt-4 text-stone-600">{content.valuesIntro}</p> : null}
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {content.values.map((v, index) => {
              const Icon = valueIcons[index % valueIcons.length];
              return (
                <div key={`${v.title}-${index}`} className="card p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-stone-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-stone-600 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-28">
        <div className="container-prose">
          <div className="mx-auto max-w-2xl text-center mb-14">
            <span className="badge-gold mb-4">Our Journey</span>
            <h2 className="font-display text-3xl font-bold text-stone-900 sm:text-4xl">
              {content.milestonesHeading}
            </h2>
            {content.milestonesIntro ? <p className="mt-4 text-stone-600">{content.milestonesIntro}</p> : null}
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-brand-200 md:-translate-x-1/2" />
            {content.milestones.map((m, i) => (
              <div key={`${m.year}-${m.title}-${i}`} className={`relative flex gap-6 md:gap-0 mb-12 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-gold-500 ring-4 ring-stone-50 md:-translate-x-1/2 mt-2" />
                <div className={`pl-12 md:pl-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="card p-6">
                    <span className="badge-gold mb-2">{m.year}</span>
                    <h3 className="font-display text-lg font-semibold text-stone-900">{m.title}</h3>
                    <p className="mt-2 text-sm text-stone-600">{m.description}</p>
                  </div>
                </div>
                <div className="hidden md:block md:w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-900">
        <div className="container-prose text-center">
          <h2 className="font-display text-3xl font-bold text-white">
            {content.ctaHeading}
          </h2>
          <Link to={content.ctaLink} className="btn-gold mt-6">
            {content.ctaButtonText}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
